import type { FC } from 'react';
import React, { createContext, useMemo, useCallback, useState } from 'react';

export type AudienceSegmentationProviderProps = {
	initialAudienceGroupIds: string[];
	isCompanyHub: boolean;
};

export type AudienceSegmentationContextValueType = {
	currentAudienceGroupIds: string[];
	setCurrentAudienceGroupIds: (groupIds: string[]) => void;
	isCompanyHub: boolean;
	isTargetAudience: (targetGroupIds: string[]) => boolean;
};

export const ACCESSIBLE_TO_ANYONE_OPTION_ID = 'accessible-to-anyone';

export const AudienceSegmentationContext = createContext<AudienceSegmentationContextValueType>({
	currentAudienceGroupIds: [],
	setCurrentAudienceGroupIds: () => {},
	isCompanyHub: false,
	isTargetAudience: () => true,
});

export const AudienceSegmentationContextProvider: FC<AudienceSegmentationProviderProps> = ({
	children,
	initialAudienceGroupIds,
	isCompanyHub,
}) => {
	const [currentAudienceGroupIds, setCurrentAudienceGroupIds] = useState(initialAudienceGroupIds);

	const isTargetAudience = useCallback(
		(targetGroupIds: string[] | undefined) => {
			if (
				!isCompanyHub || // if we are outside of company hub, no filtering
				targetGroupIds === undefined // if no target groups are provided, no filtering
			) {
				return true;
			}
			const audienceGroupIdsSet = new Set(currentAudienceGroupIds);
			if (audienceGroupIdsSet.has(ACCESSIBLE_TO_ANYONE_OPTION_ID)) {
				return targetGroupIds === undefined;
			}
			return targetGroupIds.some((allowedGroup) => audienceGroupIdsSet.has(allowedGroup));
		},
		[currentAudienceGroupIds, isCompanyHub],
	);

	const audienceSegmentationContextValue = useMemo(
		() => ({
			currentAudienceGroupIds,
			setCurrentAudienceGroupIds,
			isCompanyHub,
			isTargetAudience,
		}),
		[currentAudienceGroupIds, setCurrentAudienceGroupIds, isCompanyHub, isTargetAudience],
	);

	return (
		<AudienceSegmentationContext.Provider value={audienceSegmentationContextValue}>
			{children}
		</AudienceSegmentationContext.Provider>
	);
};
